import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { IUsageDetail } from "./interface/IUsageDetail";
import { formatUsage } from "./usageUtils";
import Select from "@material-ui/core/Select";
import { RECORDS_PER_PAGE } from "@constants";
import Typography from "@material-ui/core/Typography";
import { formatDateTimeSeconds } from "@utils/dateFormat";
import Box from "@material-ui/core/Box";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid } from "@material-ui/core";
import { HandleErrorAndLoading } from "@common/handleErrorAndLoading/HandleErrorAndLoading";
import { Pagination } from "@common/pagination/Pagination";
import { IFetchState } from "src/itsf/fetch/fetcherUtils";
import { centsToPrice, centsToPriceFourDecimals } from "src/itsf/utils/priceUtils";

interface IParams {
    usageDetailList: IUsageDetail[];
    getUsageDetailListState: IFetchState;
    currentPage: number;
    totalPages: number;
    selectedPageSize: number;
    refreshUsageDetailList(): void;
    onPageSizeChange(selectedPage: number): void;
    setCurrentPage(selectedPage: number): void;
}

export const UsageDetailList = (props: IParams) => {
    const {
        usageDetailList,
        getUsageDetailListState,
        setCurrentPage,
        currentPage,
        onPageSizeChange,
        totalPages,
        selectedPageSize,
        refreshUsageDetailList,
    } = props;
    const { t } = useTranslation("usage");

    return (
        <div className="usage-detail">
            <HandleErrorAndLoading
                error={getUsageDetailListState.error}
                isLoading={getUsageDetailListState.isLoading}
                retryFetch={refreshUsageDetailList}
            >
                {usageDetailList.length > 0 && (
                    <Fragment>
                        <Box className="table-wrapper">
                            <Table className="table-main">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Typography color="primary">{t("date")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("type")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("value")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("charge")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("number")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("location")}</Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {usageDetailList.map((row, idex: number) => (
                                        <TableRow aria-label="usage detail row" key={idex}>
                                            <TableCell>
                                                <Typography>{formatDateTimeSeconds(row.chargeStartDateTime)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{row.invoiceText}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{formatUsage(row)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>
                                                    {t("price", { price: centsToPriceFourDecimals(row.amountFourDecimals) })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{row.destinationNumber}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{row.destinationCountryCode}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <Grid alignItems="center" container>
                            <Grid item md={4} xs={12}>
                                <div className="page-select">
                                    <Typography>{t("showPerPage")}</Typography>
                                    <Select
                                        aria-label="Page size select"
                                        className="page-size"
                                        disableUnderline
                                        displayEmpty
                                        fullWidth
                                        id="selected-page-size"
                                        value={selectedPageSize}
                                        variant="filled"
                                        onChange={(event) => onPageSizeChange(parseInt(event.target.value as string))}
                                    >
                                        {RECORDS_PER_PAGE.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </Grid>
                            <Grid className="pagination-wrapper" item md={8} xs={12}>
                                <Pagination
                                    currentPage={currentPage}
                                    setCurrentIndex={setCurrentPage}
                                    totalPages={totalPages}
                                />
                            </Grid>
                        </Grid>
                    </Fragment>
                )}
                {usageDetailList.length === 0 && <Typography className="no-result">{t("noResult")}</Typography>}
            </HandleErrorAndLoading>
        </div>
    );
};
